import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  TextField,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { fetchDropdownOptions } from "../redux/dropdownoptionsSlice";
import SelectedFiltersChips from "./SelectedFilterChips";

const Filter = ({ onFilterChange, fields = [], externalFilters = null }) => {
  const dispatch = useDispatch();
  const { data = {} } = useSelector((state) => state.dropdownOptions || {});
  const statusOptionsFromStore = Array.isArray(data.status) ? data.status : [];

  const defaultStatuses = ["Complete", "Overseas", "Alloccasion", "Local"];

  // canonical defaults (status must be an array)
  const initialFilters = useMemo(
    () => ({
      donorId: "",
      name: "",
      pincode: "",
      mobile: "",
      bookNo: "",
      receiptNo: "",
      transactionId: "",
      status: fields.includes("status") ? defaultStatuses : [],
      remarks: "",
    }),
    [fields]
  );

  // only keep the fields the parent asked for (use their default values from initialFilters)
  const initialState = useMemo(() => {
    return Object.keys(initialFilters).reduce((acc, key) => {
      if (fields.includes(key)) acc[key] = initialFilters[key];
      return acc;
    }, {});
  }, [fields, initialFilters]);

  const [filters, setFilters] = useState(initialState);

  // fetch status options if not present
  useEffect(() => {
    if (statusOptionsFromStore.length === 0) {
      dispatch(fetchDropdownOptions("status"));
    }
  }, [dispatch, statusOptionsFromStore.length]);
  
  const statusOptions = statusOptionsFromStore.length
    ? statusOptionsFromStore.map((opt) => (opt && opt.value ? opt.value : opt))
    : defaultStatuses;
  
  // Check if all default statuses are selected (with null safety)
  const isGroupSelected = filters.status && Array.isArray(filters.status) 
    ? defaultStatuses.every((status) => filters.status.includes(status))
    : false;
  
  const allOptions = ["Group Select", ...statusOptions];

  // If parent passes externalFilters, coerce and merge them safely
  useEffect(() => {
    if (!externalFilters) return;

    const coerced = { ...initialState };

    Object.keys(initialFilters).forEach((k) => {
      if (!fields.includes(k)) return;
      const val = externalFilters[k];

      if (k === "status") {
        if (Array.isArray(val)) coerced[k] = val;
        else if (typeof val === "string") {
          // if it's a comma separated string, split; otherwise if empty -> []
          coerced[k] = val
            ? val
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
        } else {
          coerced[k] = initialFilters[k];
        }
      } else {
        // for other fields accept provided value or keep default
        coerced[k] = val ?? initialFilters[k];
      }
    });

    setFilters(coerced);
    onFilterChange && onFilterChange(coerced);
    // intentionally not including `initialState` in deps to avoid infinite loop
    // externalFilters and fields are correct deps
  }, [externalFilters, fields, initialFilters, onFilterChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // defensive: if status somehow comes as string, convert to array
    const newValue =
      name === "status"
        ? typeof value === "string"
          ? value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : value
        : value;
    const newFilters = { ...filters, [name]: newValue };
    setFilters(newFilters);
    onFilterChange && onFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    const resetFilters = {
      ...initialState,
      status: fields.includes("status") ? defaultStatuses : []
    };
    setFilters(resetFilters);
    onFilterChange && onFilterChange(resetFilters);
  };

  const isFilterApplied = Object.values(filters).some((v) =>
    Array.isArray(v) ? v.length > 0 : v !== "" && v != null
  );

  return (
    <Box display="flex" flexDirection={"column"} gap={2} marginBottom={2}>
      <Typography fontSize={18} fontWeight="bold" marginTop={1}>
        Search By
      </Typography>

      <Box display="flex" gap={2}>
        {fields.includes("donorId") && (
          <TextField
            label="Donor ID"
            placeholder="Search By Donor Id"
            name="donorId"
            variant="outlined"
            value={filters.donorId ?? ""}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/\D/g, "");
              handleChange({
                target: { name: "donorId", value: numericValue },
              });
            }}
            size="small"
          />
        )}

        {fields.includes("name") && (
          <TextField
            label="Name"
            placeholder="Search By Name"
            name="name"
            variant="outlined"
            value={filters.name ?? ""}
            onChange={(e) => {
              const alphaValue = e.target.value.replace(/[0-9]/g, "");
              handleChange({ target: { name: "name", value: alphaValue } });
            }}
            size="small"
          />
        )}

        {fields.includes("pincode") && (
          <TextField
            label="Pincode"
            placeholder="Search By Pincode"
            name="pincode"
            variant="outlined"
            value={filters.pincode ?? ""}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/\D/g, "");
              handleChange({
                target: { name: "pincode", value: numericValue },
              });
            }}
            size="small"
          />
        )}

        {fields.includes("mobile") && (
          <TextField
            label="Mobile"
            placeholder="Search by Mobile"
            name="mobile"
            variant="outlined"
            value={filters.mobile ?? ""}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/\D/g, "");
              handleChange({ target: { name: "mobile", value: numericValue } });
            }}
            size="small"
          />
        )}

        {fields.includes("bookNo") && (
          <TextField
            label="Book No"
            placeholder="Search by Book No"
            name="bookNo"
            variant="outlined"
            value={filters.bookNo ?? ""}
            onChange={(e) => {
              const numericValue = e.target.value;
              handleChange({ target: { name: "bookNo", value: numericValue } });
            }}
            size="small"
          />
        )}

        {fields.includes("receiptNo") && (
          <TextField
            label="Receipt No"
            placeholder="Search by Receipt No"
            name="receiptNo"
            variant="outlined"
            value={filters.receiptNo ?? ""}
            onChange={(e) => {
              const numericValue = e.target.value;
              handleChange({
                target: { name: "receiptNo", value: numericValue },
              });
            }}
            size="small"
          />
        )}

        {fields.includes("transactionId") && (
          <TextField
            label="Transaction ID"
            placeholder="Search by Transaction ID"
            name="transactionId"
            variant="outlined"
            value={filters.transactionId ?? ""}
            onChange={handleChange}
            size="small"
          />
        )}

        {fields.includes("status") && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              multiple
              name="status"
              value={filters.status || []}
              onChange={(e) => {
                const { value } = e.target;
                let resolved = typeof value === "string" ? value.split(",") : value;
                const currentStatus = filters.status || [];
                
                // Check if "Group Select" was clicked
                if (resolved.includes("Group Select")) {
                  // If group select is clicked and all defaults are already selected, deselect all defaults
                  if (isGroupSelected) {
                    const newStatus = currentStatus.filter(s => !defaultStatuses.includes(s));
                    const newFilters = { ...filters, status: newStatus };
                    setFilters(newFilters);
                    onFilterChange && onFilterChange(newFilters);
                  } else {
                    // Select all default statuses (merge with any non-default statuses already selected)
                    const nonDefaultStatuses = currentStatus.filter(s => !defaultStatuses.includes(s));
                    const newStatus = [...new Set([...nonDefaultStatuses, ...defaultStatuses])];
                    const newFilters = { ...filters, status: newStatus };
                    setFilters(newFilters);
                    onFilterChange && onFilterChange(newFilters);
                  }
                } else {
                  // Individual status selection/deselection
                  const newStatus = resolved.filter((v) => v !== "Group Select");
                  const newFilters = { ...filters, status: newStatus };
                  setFilters(newFilters);
                  onFilterChange && onFilterChange(newFilters);
                }
              }}
              renderValue={(selected) => {
                if (!selected || selected.length === 0) return "";
                const allDefaultsSelected = defaultStatuses.every(s => selected.includes(s));
                if (allDefaultsSelected && selected.length === defaultStatuses.length) {
                  return "Group Select";
                }
                return selected.join(", ");
              }}
            >
              {allOptions.map((opt) => {
                if (opt === "Group Select") {
                  return (
                    <MenuItem key="Group Select" value="Group Select">
                      <Checkbox checked={isGroupSelected} />
                      <ListItemText primary="Group Select" />
                    </MenuItem>
                  );
                } else {
                  return (
                    <MenuItem key={opt} value={opt}>
                      <Checkbox checked={filters.status ? filters.status.includes(opt) : false} />
                      <ListItemText primary={opt} />
                    </MenuItem>
                  );
                }
              })}
            </Select>
          </FormControl>
        )}

        {fields.includes("remarks") && (
          <TextField
            label="Remarks"
            placeholder="Search by Remarks"
            name="remarks"
            variant="outlined"
            value={filters.remarks ?? ""}
            onChange={handleChange}
            size="small"
          />
        )}
        {isFilterApplied && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              color="error"
              onClick={handleClearFilters}
              value="Clear"
              endIcon={<SearchOffIcon />}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignContent: "flex-end",
                textTransform: "none",
                fontWeight: "bold",
                borderRadius: "8px",
                px: 2,
                py: 1,
                ":hover": { backgroundColor: "#b71c1c" },
              }}
            ></Button>
          </div>
        )}
      </Box>

      <SelectedFiltersChips
        filters={filters}
        onRemoveFilter={(field) => {
          const newFilters = {
            ...filters,
            [field]: Array.isArray(filters[field]) ? [] : "",
          };
          setFilters(newFilters);
          onFilterChange && onFilterChange(newFilters);
        }}
      />
    </Box>
  );
};

export default Filter;